<template>
  <v-dialog v-bind:value="open" @click:outside="cancel" max-width="1200px" scrollable persistent>
    <v-card>
      <v-card-title>
        <span class="headline">Puesta en marcha contable</span>
      </v-card-title>

      <v-card-text>
        <v-alert dense text type="info" class="caption mb-4">
          La empresa ya venía operando sin contabilidad, así que las cuentas patrimoniales
          arrancarían en cero. Para evitarlo se fija una <b>fecha de corte</b> y se genera un
          <b>asiento de apertura</b> con los saldos a esa fecha. El detalle por cliente y por
          comprobante no se pierde: sigue en las cuentas corrientes.
        </v-alert>

        <!-- 1. Fecha de corte -->
        <div class="row align-center">
          <div class="col-md-5">
            <v-menu
              ref="fecha_menu"
              v-model="fecha_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fecha_inicio"
                  label="Fecha de inicio contable*"
                  prepend-icon="mdi-calendar"
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fecha_inicio"
                :max="hoy"
                no-title
                scrollable
                @input="onFechaChange"
              ></v-date-picker>
            </v-menu>
          </div>
          <div class="col-md-7 caption grey--text text--darken-1">
            <template v-if="data && data.ejercicio">
              Ejercicio en curso: <b>{{ data.ejercicio.name }}</b>
              ({{ data.ejercicio.date_from }} a {{ data.ejercicio.date_to }}).
              <a href="#" @click.prevent="usarSugerida">Usar el inicio del ejercicio</a>
              para obtener el Estado de Resultados completo.
            </template>
            <template v-else>
              No hay un ejercicio cargado para este emisor. Se sugiere la fecha de hoy.
            </template>
          </div>
        </div>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="my-4"></v-progress-linear>

        <template v-if="data && !loading">
          <!-- 2. Backfill -->
          <v-alert
            v-if="data.requiere_backfill"
            dense
            text
            :type="preflightOk ? 'success' : 'error'"
            class="caption mt-4 mb-2"
          >
            <template v-if="preflightOk">
              Se generarán los asientos de los documentos con fecha desde el
              {{ data.fecha_inicio }}. Se verificaron
              <b>{{ data.preflight.generados }}</b> documento(s) y todos se pueden asentar.
            </template>
            <template v-else-if="data.preflight">
              Hay <b>{{ data.preflight.irresolubles.length }}</b> documento(s) en el período
              que el motor no puede asentar. Corregí el mapeo antes de habilitar — si no,
              quedarían huecos invisibles en el Mayor.
            </template>
            <template v-else>
              La fecha es retroactiva. Completá el mapeo de cuentas para poder verificar los
              documentos del período.
            </template>
          </v-alert>

          <div
            v-if="data.preflight && data.preflight.irresolubles.length > 0"
            class="preflight-errores mb-4"
          >
            <div
              v-for="(err, i) in data.preflight.irresolubles"
              :key="i"
              class="caption red--text py-1"
            >
              <b>{{ err.documento }}</b> — {{ err.motivo }}
            </div>
          </div>

          <!-- 3. Saldos iniciales -->
          <div class="subtitle-2 font-weight-bold mt-4 mb-1">
            Saldos iniciales al {{ data.fecha_inicio }}
          </div>
          <div class="caption grey--text text--darken-1 mb-2">
            Los importes van en positivo del lado natural de la cuenta (Activo deudor,
            Pasivo y PN acreedor). Los sugeridos salen de tesorería y de las cuentas
            corrientes; el resto (stock, bienes de uso, capital, préstamos) los cargás vos.
          </div>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="min-width: 320px">Cuenta</th>
                  <th class="text-left">Origen</th>
                  <th class="text-right" style="width: 220px">Importe</th>
                  <th style="width: 40px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(fila, i) in saldos" :key="i">
                  <td>
                    <v-combobox
                      v-model="fila.account"
                      :items="accountsPatrimoniales"
                      item-text="label"
                      item-value="id"
                      dense
                      hide-details
                      class="pt-0 mt-0"
                      return-object
                    >
                      <!-- El grupo aclara la naturaleza: "Resultados Acumulados" es PN,
                           no una cuenta de resultado, y el nombre solo confunde. -->
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.label }}</v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ grupoNombre(item.grupo) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </td>
                  <td class="caption grey--text origen" :title="origenLabel(fila)">
                    {{ origenLabel(fila) }}
                  </td>
                  <td>
                    <!-- Lectura: el importe formateado se lee de un vistazo; el input crudo no. -->
                    <div v-if="!fila.editing" class="d-flex align-center justify-end">
                      <span class="importe">{{ formatNumber(fila.importe) }}</span>
                      <v-icon small color="grey darken-1" class="ml-2" @click="editar(fila, i)">edit</v-icon>
                    </div>
                    <v-text-field
                      v-else
                      :ref="'importe_' + i"
                      v-model="fila.importe"
                      type="number"
                      dense
                      hide-details
                      reverse
                      class="pt-0 mt-0"
                      @blur="fila.editing = false"
                      @keyup.enter="fila.editing = false"
                    ></v-text-field>
                  </td>
                  <td class="text-center">
                    <v-icon small color="grey" @click="saldos.splice(i, 1)">delete</v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-btn text small color="primary" class="mt-2" @click="agregarFila">
            <v-icon left small>add</v-icon> Agregar cuenta
          </v-btn>

          <!-- 4. Diferencia -->
          <v-divider class="my-3"></v-divider>
          <div class="d-flex justify-space-between align-center">
            <div class="caption grey--text text--darken-1">
              La diferencia entre lo que cargaste al Debe y al Haber se imputa a
              <b>3.2.01 Resultados Acumulados</b>, una cuenta de <b>Patrimonio Neto</b>
              (no de resultado). No es un cálculo: por la ecuación contable
              <i>Activo − Pasivo = Patrimonio Neto</i>, lo que sobra <b>es</b> tu PN a la
              fecha de corte — resultados de ejercicios anteriores, aportes y ajustes que
              nunca pasaron por esta contabilidad, y que por eso no se pueden desagregar.
            </div>
            <div class="text-right ml-4 plug">
              <div class="caption grey--text">
                {{ diferencia >= 0 ? "Al Haber de" : "Al Debe de" }} 3.2.01 Resultados Acumulados (PN)
              </div>
              <div class="subtitle-1 font-weight-bold">{{ formatMoney(diferencia) }}</div>
            </div>
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancelar</v-btn>
        <v-btn color="success" :disabled="!puedeHabilitar" :loading="saving" @click="aceptar">
          Habilitar contabilidad
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { contabilidadService } from "@/libs/ws/contabilidadService";

export default {
  props: {
    open: { type: Boolean, default: false },
    emitter: { type: Object, default: null },
    accounts: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      saving: false,
      fecha_menu: false,
      fecha_inicio: null,
      data: null,
      saldos: [],
    };
  },
  computed: {
    hoy() {
      return new Date().toISOString().slice(0, 10);
    },
    accountsPatrimoniales() {
      // La apertura solo admite cuentas patrimoniales: una de resultado inyectaría
      // ingresos o gastos de antes del corte en el ejercicio nuevo.
      return this.accounts.filter((a) => !a.is_resultado);
    },
    /**
     * Debe − Haber de lo cargado: lo que falta para balancear va a Resultados Acumulados.
     *
     * Se devuelve en la misma convención de signo natural que las filas (PN acreedor =
     * positivo). Si el resultado es > 0 el plug va al Haber, que es lo normal: un PN
     * acreedor. Un negativo significa PN negativo — pasivo mayor que activo.
     */
    diferencia() {
      let total = 0;
      this.saldos.forEach((f) => {
        if (!f.account) return;
        const importe = parseFloat(f.importe) || 0;
        total += f.account.grupo === 1 ? importe : -importe;
      });
      return total;
    },
    preflightOk() {
      return (
        this.data &&
        this.data.preflight &&
        this.data.preflight.irresolubles.length === 0
      );
    },
    puedeHabilitar() {
      if (!this.data || this.loading || !this.data.completo) return false;
      if (this.data.requiere_backfill && !this.preflightOk) return false;
      return this.saldos.some((f) => f.account && parseFloat(f.importe));
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.fecha_inicio = null;
        this.load();
      }
    },
  },
  methods: {
    load() {
      if (!this.emitter) return;
      this.loading = true;
      contabilidadService
        .puestaEnMarcha({
          emitter_id: this.emitter.id,
          fecha_inicio: this.fecha_inicio,
        })
        .then((response) => {
          if (!response.status) {
            this.$toast.error(response.msg, "Error");
            return;
          }
          this.data = response.result;
          this.fecha_inicio = this.data.fecha_inicio;
          this.saldos = (this.data.saldos || []).map((s) => ({
            account: this.buscarAccount(s.account.id),
            importe: s.importe,
            fuente: s.fuente,
            detalle: s.detalle,
            editing: false, // declarado acá para que Vue 2 lo haga reactivo
          }));
        })
        .catch((e) => {
          this.$toast.error(e.msg || "Error al calcular los saldos iniciales", "Error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onFechaChange() {
      this.fecha_menu = false;
      this.load();
    },
    usarSugerida() {
      this.fecha_inicio = this.data.fecha_sugerida;
      this.load();
    },
    buscarAccount(id) {
      return this.accounts.find((a) => a.id === id) || null;
    },
    agregarFila() {
      // Una fila nueva nace en edición: se agrega justamente para tipear un importe.
      this.saldos.push({ account: null, importe: 0, fuente: null, detalle: [], editing: true });
    },
    editar(fila, index) {
      fila.editing = true;
      this.$nextTick(() => {
        const ref = this.$refs["importe_" + index];
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) input.focus();
      });
    },
    grupoNombre(grupo) {
      return { 1: "Activo", 2: "Pasivo", 3: "Patrimonio Neto" }[grupo] || "";
    },
    origenLabel(fila) {
      if (!fila.fuente) return "Carga manual";
      return fila.detalle && fila.detalle.length ? fila.detalle.join(" · ") : fila.fuente;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(value) || 0);
    },
    formatMoney(value) {
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
      }).format(value || 0);
    },
    aceptar() {
      this.saving = true;
      contabilidadService
        .habilitar({
          emitter_id: this.emitter.id,
          fecha_inicio: this.fecha_inicio,
          saldos: this.saldos
            .filter((f) => f.account && parseFloat(f.importe))
            .map((f) => ({ account_id: f.account.id, importe: parseFloat(f.importe) })),
        })
        .then((response) => {
          if (!response.status) {
            this.$toast.error(response.msg, "Error");
            return;
          }
          const generados = response.result.asientos_generados;
          this.$toast.success(
            generados > 0
              ? "Contabilidad habilitada. Se generaron " + generados + " asientos del período."
              : "Contabilidad habilitada.",
            "OK"
          );
          this.$emit("accept");
        })
        .catch((e) => {
          this.$toast.error(e.msg || "Error al habilitar la contabilidad", "Error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
/* Tabular para que los importes de distintas filas queden alineados por columna. */
/* La tabla es dense (32px por fila): con el combobox y el lápiz adentro queda apretada.
   El !important es necesario porque la regla de Vuetify tiene más especificidad. */
td {
  height: 48px !important;
}
.importe {
  font-variant-numeric: tabular-nums;
  font-size: 0.95rem;
}
/* Sin nowrap el signo negativo se corta a la línea anterior y se lee como si no estuviera. */
.plug {
  min-width: 260px;
  white-space: nowrap;
}
/* El detalle del origen puede ser largo: se recorta para no comprimir la columna de cuentas. */
.origen {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.preflight-errores {
  max-height: 160px;
  overflow-y: auto;
  border-left: 3px solid #f44336;
  padding-left: 10px;
}
</style>
